import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MessageCircle, Server, Hash, TestTube, Check, X, Loader2, Newspaper, Calendar } from 'lucide-react';
import { discordAPI } from '../services/api';
import { toast } from 'sonner';

const DiscordConfig = () => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [guilds, setGuilds] = useState([]);
  const [channels, setChannels] = useState([]);
  const [selectedGuild, setSelectedGuild] = useState('');
  const [newsChannelId, setNewsChannelId] = useState('');
  const [eventsChannelId, setEventsChannelId] = useState('');
  const [testing, setTesting] = useState(null); // 'news' or 'events'
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    if (selectedGuild) {
      loadChannels(selectedGuild);
    }
  }, [selectedGuild]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const data = await discordAPI.getConfig();
      setConfig(data);
      setNewsChannelId(data.news_channel_id || '');
      setEventsChannelId(data.events_channel_id || '');
      
      if (data.bot_configured) {
        const guildsData = await discordAPI.getGuilds();
        setGuilds(guildsData.guilds || []);
      }
    } catch (error) {
      console.error('Error loading Discord config:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadChannels = async (guildId) => {
    try {
      const data = await discordAPI.getGuildChannels(guildId);
      setChannels(data.channels || []);
    } catch (error) {
      console.error('Error loading channels:', error);
      toast.error('Erreur lors du chargement des channels');
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      await discordAPI.updateConfig(newsChannelId, eventsChannelId);
      toast.success('Configuration Discord sauvegardée !');
      loadConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (channelType) => {
    try {
      setTesting(channelType);
      await discordAPI.test(channelType);
      toast.success(`Message de test envoyé sur le channel ${channelType === 'news' ? 'Actualités' : 'Événements'} !`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du test');
    } finally {
      setTesting(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-6 bg-black/30 border border-purple-500/20">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 text-purple-400 animate-spin" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-black/30 border border-purple-500/20">
      <div className="flex items-center mb-6">
        <MessageCircle className="h-6 w-6 text-indigo-400 mr-3" />
        <h3 className="text-lg font-bold text-purple-300">Configuration Discord</h3>
      </div>

      {!config?.bot_configured ? (
        <div className="text-center py-6">
          <X className="h-12 w-12 text-red-400 mx-auto mb-3" />
          <p className="text-gray-400">Bot Discord non configuré</p>
          <p className="text-sm text-gray-500 mt-2">
            Ajoutez le DISCORD_BOT_TOKEN dans les variables d'environnement
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Status */}
          <div className="flex items-center p-3 bg-green-900/20 rounded-lg border border-green-500/30">
            <Check className="h-5 w-5 text-green-400 mr-2" />
            <span className="text-green-300 text-sm">Bot Discord connecté</span>
          </div>

          {/* Guild Selection */}
          <div className="space-y-2">
            <label className="text-sm text-purple-300 flex items-center">
              <Server className="h-4 w-4 mr-2" />
              Serveur Discord
            </label>
            <Select value={selectedGuild} onValueChange={setSelectedGuild}>
              <SelectTrigger className="bg-black/50 border-purple-500/30 text-white">
                <SelectValue placeholder="Sélectionner un serveur..." />
              </SelectTrigger>
              <SelectContent className="bg-black border-purple-500/30">
                {guilds.map((guild) => (
                  <SelectItem key={guild.id} value={guild.id} className="text-purple-300">
                    {guild.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedGuild && (
            <>
              {/* News Channel Selection */}
              <div className="space-y-2">
                <label className="text-sm text-purple-300 flex items-center">
                  <Newspaper className="h-4 w-4 mr-2" />
                  Channel Actualités
                </label>
                <div className="flex gap-2">
                  <Select value={newsChannelId} onValueChange={setNewsChannelId}>
                    <SelectTrigger className="bg-black/50 border-purple-500/30 text-white flex-1">
                      <SelectValue placeholder="Sélectionner un channel..." />
                    </SelectTrigger>
                    <SelectContent className="bg-black border-purple-500/30 max-h-[200px]">
                      {channels.map((channel) => (
                        <SelectItem key={channel.id} value={channel.id} className="text-purple-300">
                          # {channel.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={() => handleTest('news')}
                    disabled={!newsChannelId || testing === 'news'}
                    size="sm"
                    variant="outline"
                    className="border-green-500/50 text-green-400 hover:bg-green-900/30"
                  >
                    {testing === 'news' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <TestTube className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Events Channel Selection */}
              <div className="space-y-2">
                <label className="text-sm text-purple-300 flex items-center">
                  <Calendar className="h-4 w-4 mr-2" />
                  Channel Événements
                </label>
                <div className="flex gap-2">
                  <Select value={eventsChannelId} onValueChange={setEventsChannelId}>
                    <SelectTrigger className="bg-black/50 border-purple-500/30 text-white flex-1">
                      <SelectValue placeholder="Sélectionner un channel..." />
                    </SelectTrigger>
                    <SelectContent className="bg-black border-purple-500/30 max-h-[200px]">
                      {channels.map((channel) => (
                        <SelectItem key={channel.id} value={channel.id} className="text-purple-300">
                          # {channel.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={() => handleTest('events')}
                    disabled={!eventsChannelId || testing === 'events'}
                    size="sm"
                    variant="outline"
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-900/30"
                  >
                    {testing === 'events' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <TestTube className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* Current Channels Info */}
          {(config?.news_channel_id || config?.events_channel_id) && (
            <div className="p-3 bg-purple-900/20 rounded-lg border border-purple-500/30 space-y-1">
              {config?.news_channel_id && (
                <p className="text-sm text-purple-300">
                  📰 Actualités: <span className="font-mono text-purple-400">{config.news_channel_id}</span>
                </p>
              )}
              {config?.events_channel_id && (
                <p className="text-sm text-purple-300">
                  🎲 Événements: <span className="font-mono text-purple-400">{config.events_channel_id}</span>
                </p>
              )}
            </div>
          )}

          {/* Save Button */}
          <Button
            onClick={handleSaveConfig}
            disabled={saving}
            className="w-full bg-purple-600 hover:bg-purple-500"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Sauvegarder
          </Button>

          {/* Info */}
          <div className="mt-4 p-3 bg-black/30 rounded-lg border border-gray-700">
            <p className="text-xs text-gray-400">
              Les actualités et événements seront automatiquement postés sur leurs channels respectifs.
            </p>
          </div>
        </div>
      )}
    </Card>
  );
};

export default DiscordConfig;
